import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PostCard from "@/components/PostCard";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";
import { fileUrl } from "@/lib/api";
import { toast } from "sonner";

export default function PostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");

  const load = useCallback(() => {
    api.get(`/posts/${id}`).then(({ data }) => setPost(data)).catch(() => setPost(false));
    api.get(`/posts/${id}/comments`).then(({ data }) => setComments(data)).catch(() => {});
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const onCheckout = (p) => {
    if (!user) return navigate("/login");
    if (p.visibility === "ppv") startCheckout({ kind: "ppv", resource_id: p.id });
    else startCheckout({ kind: "subscription", resource_id: p.creator.id });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return navigate("/login");
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/posts/${id}/comments`, { text });
      setComments([data, ...comments]);
      setText("");
    } catch { toast.error("Could not post comment"); }
  };

  if (post === false) return <div className="py-32 text-center font-display text-4xl">Post not found</div>;
  if (!post) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-2xl mx-auto px-5 py-10" data-testid="post-detail">
      <PostCard post={post} onCheckout={onCheckout} />

      <div className="mt-8">
        <h2 className="font-accent text-sm uppercase tracking-widest text-muted-foreground mb-4">Comments ({comments.length})</h2>
        <form onSubmit={submit} className="flex gap-2 mb-6" data-testid="comment-form">
          <input data-testid="comment-input" value={text} onChange={(e) => setText(e.target.value)}
            placeholder={user ? "Add a comment…" : "Log in to comment"} disabled={!user}
            className="flex-1 bg-card border border-border px-4 py-3 focus:border-primary outline-none transition-colors disabled:opacity-60" />
          <button data-testid="comment-submit" disabled={!user}
            className="px-6 bg-primary text-primary-foreground font-accent font-bold uppercase text-xs tracking-widest transition-transform active:scale-95 disabled:opacity-60">Post</button>
        </form>
        <div className="flex flex-col gap-4">
          {comments.map((c) => (
            <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
              <div className="h-9 w-9 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center overflow-hidden shrink-0">
                {c.user?.avatar_id ? <img src={fileUrl(c.user.avatar_id)} alt="" className="h-full w-full object-cover" />
                  : <span className="text-sm font-accent font-bold">{(c.user?.display_name || "?")[0]}</span>}
              </div>
              <div className="bg-card border border-border px-4 py-2 flex-1">
                <p className="font-accent font-bold text-sm">{c.user?.display_name}</p>
                <p className="text-sm text-foreground/90">{c.text}</p>
              </div>
            </div>
          ))}
          {comments.length === 0 && <p className="text-muted-foreground text-sm">Be the first to comment.</p>}
        </div>
      </div>
    </div>
  );
}
